import { useEffect, useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import {
  CarFront,
  Check,
  ChevronRight,
  Minus,
  Navigation,
  Plus,
  Sparkles,
  Trash2,
  Users,
} from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { LocationSearchModal } from '@/components/location/LocationSearchModal'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { useTheme } from '@/lib/hooks/use-theme'
import { selection } from '@/lib/utils/haptics'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import { MIN_TOUR_PASSENGERS } from '@/lib/tours/booking-rules'
import type {
  TourCustomQuoteInput,
  TourCustomQuoteLocation,
  TourCustomQuoteResult,
  TourCustomQuoteVehicleType,
} from '@/lib/types/tours.types'

const VEHICLES: ReadonlyArray<{
  type: TourCustomQuoteVehicleType
  seats: number
  labelKey: string
  detailKey: string
}> = [
  {
    type: 'microbus',
    seats: 15,
    labelKey: 'vehicle_microbus',
    detailKey: 'vehicle_microbus_detail',
  },
  { type: 'coaster', seats: 26, labelKey: 'vehicle_coaster', detailKey: 'vehicle_coaster_detail' },
  { type: 'bus', seats: 45, labelKey: 'vehicle_bus', detailKey: 'vehicle_bus_detail' },
]

type PickerTarget = 'origin' | 'destination' | number | null

function plannerErrorKey(code: string | undefined): string {
  switch (code) {
    case 'TOURS_NATIONAL_ROUTE_REQUIRED':
      return 'tours.custom_quote.error_national'
    case 'TOURS_ROUTE_UNAVAILABLE':
      return 'tours.custom_quote.error_route'
    case 'TOURS_CAPACITY_EXCEEDED':
      return 'tours.custom_quote.error_capacity'
    case 'TOURS_CUSTOM_QUOTE_CONFIG':
      return 'tours.custom_quote.error_unavailable'
    case 'TOURS_CUSTOM_QUOTE_CONFLICT':
      return 'tours.custom_quote.error_conflict'
    default:
      return 'tours.custom_quote.error'
  }
}

function PlaceRow({
  label,
  value,
  tone,
  testID,
  onPress,
  onRemove,
}: {
  label: string
  value: TourCustomQuoteLocation | null
  tone: 'origin' | 'stop' | 'destination'
  testID: string
  onPress: () => void
  onRemove?: () => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const dot =
    tone === 'origin'
      ? COLORS.purple.DEFAULT
      : tone === 'destination'
        ? COLORS.coral.DEFAULT
        : COLORS.amber.DEFAULT
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
      <View style={{ width: 20, alignItems: 'center' }}>
        <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: dot }} />
      </View>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={label}
        testID={testID}
        style={({ pressed }) => ({
          flex: 1,
          minWidth: 0,
          opacity: pressed ? 0.78 : 1,
        })}
      >
        <GlassSurface
          tone={value ? 'regular' : 'quiet'}
          radius={RADIUS.md}
          style={{
            minHeight: INTERACTION.minTouchTarget,
          }}
          contentStyle={{
            paddingHorizontal: SPACING.md,
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.sm,
          }}
        >
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
              {label}
            </Text>
            <Text
              numberOfLines={1}
              style={{
                marginTop: 1,
                fontFamily: FONTS.bodyBold,
                fontSize: 14,
                color: value ? colors.textPrimary : colors.textMuted,
              }}
            >
              {value?.label ?? t('tours.custom_quote.choose_place')}
            </Text>
          </View>
          <ChevronRight size={18} color={colors.textSecondary} strokeWidth={2.1} />
        </GlassSurface>
      </Pressable>
      {onRemove ? (
        <Pressable
          onPress={onRemove}
          accessibilityRole="button"
          accessibilityLabel={t('tours.custom_quote.remove_stop')}
          testID={`${testID}-remove`}
          style={({ pressed }) => ({
            width: INTERACTION.minTouchTarget,
            height: INTERACTION.minTouchTarget,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: pressed ? 0.7 : 1,
          })}
        >
          <GlassSurface
            tone="quiet"
            radius={INTERACTION.minTouchTarget / 2}
            style={{
              width: INTERACTION.minTouchTarget,
              height: INTERACTION.minTouchTarget,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Trash2 size={17} color={colors.textSecondary} strokeWidth={2} />
          </GlassSurface>
        </Pressable>
      ) : null}
    </View>
  )
}

export function TourCustomQuotePlanner({
  onBack,
  onQuote,
}: {
  onBack: () => void
  onQuote: (input: TourCustomQuoteInput) => Promise<TourCustomQuoteResult>
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [origin, setOrigin] = useState<TourCustomQuoteLocation | null>(null)
  const [destination, setDestination] = useState<TourCustomQuoteLocation | null>(null)
  const [stops, setStops] = useState<TourCustomQuoteLocation[]>([])
  const [passengerCount, setPassengerCount] = useState(MIN_TOUR_PASSENGERS)
  const [vehicleType, setVehicleType] = useState<TourCustomQuoteVehicleType>('microbus')
  const [pickerTarget, setPickerTarget] = useState<PickerTarget>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const compatibleVehicles = useMemo(
    () => VEHICLES.filter((vehicle) => vehicle.seats >= passengerCount),
    [passengerCount],
  )
  const selectedVehicle =
    compatibleVehicles.find((vehicle) => vehicle.type === vehicleType) ?? compatibleVehicles[0]

  useEffect(() => {
    if (selectedVehicle && selectedVehicle.type !== vehicleType)
      setVehicleType(selectedVehicle.type)
  }, [selectedVehicle, vehicleType])

  const pickerTitle =
    pickerTarget === 'origin'
      ? t('tours.custom_quote.origin')
      : pickerTarget === 'destination'
        ? t('tours.custom_quote.destination')
        : t('tours.custom_quote.stop', {
            count: typeof pickerTarget === 'number' ? pickerTarget + 1 : 1,
          })

  const selectPlace = (location: TourCustomQuoteLocation) => {
    if (pickerTarget === 'origin') setOrigin(location)
    else if (pickerTarget === 'destination') setDestination(location)
    else if (typeof pickerTarget === 'number') {
      setStops((current) =>
        current.map((stop, index) => (index === pickerTarget ? location : stop)),
      )
    }
    void selection()
    setPickerTarget(null)
  }

  const addStop = () => {
    if (stops.length >= 2) return
    setStops((current) => [...current, { label: '', latitude: 0, longitude: 0 }])
    setPickerTarget(stops.length)
  }

  const removeStop = (index: number) => {
    setStops((current) => current.filter((_, currentIndex) => currentIndex !== index))
    void selection()
  }

  const readyStops = stops.filter((stop) => Boolean(stop.label))
  const canQuote = Boolean(
    origin && destination && readyStops.length === stops.length && selectedVehicle,
  )

  const submit = async () => {
    if (!origin || !destination || !selectedVehicle || !canQuote) return
    setIsSubmitting(true)
    setErrorKey(null)
    try {
      await onQuote({
        idempotencyKey: `tourquote-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        origin,
        destination,
        stops: readyStops,
        passengerCount,
        vehicleType: selectedVehicle.type,
      })
    } catch (error) {
      setErrorKey(plannerErrorKey((error as { code?: string }).code))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ScreenContainer variant="content" scroll={false}>
      <View style={{ flex: 1 }}>
        <ScreenHeader
          title={t('tours.custom_quote.title')}
          subtitle={t('tours.custom_quote.subtitle')}
          showBack
          onBackPress={onBack}
          style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.md }}
        />
        <ScrollView
          style={{ flex: 1 }}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{
            paddingHorizontal: SPACING.lg,
            paddingTop: SPACING.md,
            // The quote action is a fixed decision surface below the scroll;
            // leave enough runway for the final vehicle card to clear it.
            paddingBottom: SPACING['2xl'],
            gap: SPACING.lg,
          }}
          showsVerticalScrollIndicator={false}
        >
          <GlassSurface
            tone="tint"
            tintColor={isDark ? '#6C4C9B' : '#E7D9FA'}
            radius={RADIUS.xl}
            contentStyle={{
              paddingHorizontal: SPACING.md,
              paddingVertical: SPACING.sm + 2,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
            }}
          >
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: COLORS.purple.DEFAULT,
              }}
            >
              <Navigation size={16} color={COLORS.white} strokeWidth={2.1} />
            </View>
            <Text
              style={{
                flex: 1,
                fontFamily: FONTS.body,
                fontSize: 12,
                lineHeight: 17,
                color: colors.textSecondary,
              }}
            >
              {t('tours.custom_quote.national_note')}
            </Text>
          </GlassSurface>

          <View style={{ gap: SPACING.sm }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}>
              {t('tours.custom_quote.itinerary')}
            </Text>
            <PlaceRow
              label={t('tours.custom_quote.origin')}
              value={origin}
              tone="origin"
              testID="tours.custom-quote-origin"
              onPress={() => setPickerTarget('origin')}
            />
            {stops.map((stop, index) => (
              <PlaceRow
                key={`${index}:${stop.label}`}
                label={t('tours.custom_quote.stop', { count: index + 1 })}
                value={stop.label ? stop : null}
                tone="stop"
                testID={`tours.custom-quote-stop-${index + 1}`}
                onPress={() => setPickerTarget(index)}
                onRemove={() => removeStop(index)}
              />
            ))}
            {stops.length < 2 ? (
              <Pressable
                onPress={addStop}
                accessibilityRole="button"
                accessibilityLabel={t('tours.custom_quote.add_stop')}
                testID="tours.custom-quote-add-stop"
                style={({ pressed }) => ({
                  alignSelf: 'flex-start',
                  minHeight: INTERACTION.minTouchTarget,
                  paddingHorizontal: SPACING.sm,
                  borderRadius: RADIUS.full,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.xs,
                  opacity: pressed ? 0.68 : 1,
                })}
              >
                <Plus size={18} color={COLORS.coral.DEFAULT} strokeWidth={2.3} />
                <Text
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textAccent }}
                >
                  {t('tours.custom_quote.add_stop')}
                </Text>
              </Pressable>
            ) : null}
            <PlaceRow
              label={t('tours.custom_quote.destination')}
              value={destination}
              tone="destination"
              testID="tours.custom-quote-destination"
              onPress={() => setPickerTarget('destination')}
            />
          </View>

          <View style={{ gap: SPACING.sm }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}>
              {t('tours.passengers')}
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
              <Pressable
                onPress={() =>
                  setPassengerCount((current) => Math.max(MIN_TOUR_PASSENGERS, current - 1))
                }
                disabled={passengerCount <= MIN_TOUR_PASSENGERS}
                accessibilityRole="button"
                accessibilityLabel={t('tours.decrease_passengers')}
                testID="tours.custom-quote-passenger-minus"
                style={({ pressed }) => ({
                  width: INTERACTION.minTouchTarget,
                  height: INTERACTION.minTouchTarget,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: passengerCount <= MIN_TOUR_PASSENGERS ? 0.42 : pressed ? 0.72 : 1,
                })}
              >
                <GlassSurface
                  tone="regular"
                  radius={INTERACTION.minTouchTarget / 2}
                  style={{
                    width: INTERACTION.minTouchTarget,
                    height: INTERACTION.minTouchTarget,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Minus size={20} color={colors.textPrimary} strokeWidth={2.1} />
                </GlassSurface>
              </Pressable>
              <View style={{ minWidth: 56, alignItems: 'center' }}>
                <Text
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 22, color: colors.textPrimary }}
                >
                  {passengerCount}
                </Text>
                <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
                  {t('tours.trip.people_short')}
                </Text>
              </View>
              <Pressable
                onPress={() => setPassengerCount((current) => Math.min(45, current + 1))}
                disabled={passengerCount >= 45}
                accessibilityRole="button"
                accessibilityLabel={t('tours.increase_passengers')}
                testID="tours.custom-quote-passenger-plus"
                style={({ pressed }) => ({
                  width: INTERACTION.minTouchTarget,
                  height: INTERACTION.minTouchTarget,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: passengerCount >= 45 ? 0.42 : pressed ? 0.72 : 1,
                })}
              >
                <GlassSurface
                  tone="regular"
                  radius={INTERACTION.minTouchTarget / 2}
                  style={{
                    width: INTERACTION.minTouchTarget,
                    height: INTERACTION.minTouchTarget,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={20} color={colors.textPrimary} strokeWidth={2.1} />
                </GlassSurface>
              </Pressable>
              <Text
                style={{
                  flex: 1,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
              >
                {t('tours.passenger_range', { min: MIN_TOUR_PASSENGERS, max: 45 })}
              </Text>
            </View>
          </View>

          <View style={{ gap: SPACING.sm }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}>
              {t('tours.custom_quote.transport')}
            </Text>
            <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
              {t('tours.custom_quote.vehicle_choice_hint')}
            </Text>
            {VEHICLES.map((vehicle) => {
              const compatible = vehicle.seats >= passengerCount
              const selected = selectedVehicle?.type === vehicle.type
              return (
                <Pressable
                  key={vehicle.type}
                  onPress={() => compatible && setVehicleType(vehicle.type)}
                  disabled={!compatible}
                  accessibilityRole="radio"
                  accessibilityState={{ selected, disabled: !compatible }}
                  accessibilityLabel={t(`tours.custom_quote.${vehicle.labelKey}`)}
                  testID={`tours.custom-quote-vehicle-${vehicle.type}`}
                  style={({ pressed }) => ({
                    opacity: compatible ? (pressed ? 0.76 : 1) : 0.42,
                  })}
                >
                  <GlassSurface
                    tone={selected ? 'tint' : 'regular'}
                    tintColor={selected ? COLORS.coral.DEFAULT : undefined}
                    radius={RADIUS.lg}
                    style={{
                      minHeight: 64,
                    }}
                    contentStyle={{
                      paddingHorizontal: SPACING.md,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: SPACING.sm,
                    }}
                  >
                    <View
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 18,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: selected ? COLORS.coral.DEFAULT : colors.surfaceSubtle,
                      }}
                    >
                      <CarFront
                        size={20}
                        color={selected ? COLORS.white : colors.textPrimary}
                        strokeWidth={2}
                      />
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 15,
                          color: colors.textPrimary,
                        }}
                      >
                        {t(`tours.custom_quote.${vehicle.labelKey}`)}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={{
                          marginTop: 1,
                          fontFamily: FONTS.body,
                          fontSize: 12,
                          color: colors.textSecondary,
                        }}
                      >
                        {t(`tours.custom_quote.${vehicle.detailKey}`)}
                      </Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <Users size={16} color={colors.textSecondary} strokeWidth={2} />
                      <Text
                        style={{
                          marginTop: 2,
                          fontFamily: FONTS.bodyBold,
                          fontSize: 12,
                          color: colors.textSecondary,
                        }}
                      >
                        {vehicle.seats}
                      </Text>
                    </View>
                    {selected ? (
                      <Check size={18} color={COLORS.coral.DEFAULT} strokeWidth={2.4} />
                    ) : null}
                  </GlassSurface>
                </Pressable>
              )
            })}
          </View>
        </ScrollView>

        <GlassSurface
          tone="regular"
          radius={RADIUS.xl}
          contentStyle={{
            paddingHorizontal: SPACING.lg,
            paddingTop: SPACING.sm,
            paddingBottom: SPACING.md,
            gap: SPACING.xs,
          }}
        >
          {errorKey ? (
            <Text
              accessibilityRole="alert"
              style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textError }}
            >
              {t(errorKey)}
            </Text>
          ) : null}
          <Button
            title={t('tours.custom_quote.get_quote')}
            size="lg"
            loading={isSubmitting}
            disabled={!canQuote}
            onPress={() => void submit()}
            icon={<Sparkles size={18} />}
            testID="tours.custom-quote-submit"
          />
        </GlassSurface>

        <LocationSearchModal
          visible={pickerTarget !== null}
          title={pickerTitle}
          onClose={() => setPickerTarget(null)}
          onPick={selectPlace}
          testIDPrefix="tours.custom-quote-place"
          placeholder={t('tours.custom_quote.search_placeholder')}
          searchingLabel={t('tours.custom_quote.searching')}
          emptyLabel={t('tours.custom_quote.no_places')}
          errorLabel={t('tours.custom_quote.search_error')}
        />
      </View>
    </ScreenContainer>
  )
}
